import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { getDeviceClientId, handleUnverifiedDeviceClient } from "@/utils/deviceClient";

const env = process.env;
const API_BASE_URL =
  env.VUE_APP_MODE === "development"
    ? env.VUE_APP_API_DEV_URL
    : env.VUE_APP_API_BASE_URL;


const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 
    "Content-Type": "application/json",
    "X-CLIENT-ID": getDeviceClientId()
  },
  timeout: 10000,
});

const AUTHORIZATION_TOKEN_DEBUG_ENABLED = false; // Make it true to enable token debug

let isRefreshing = false;
let refreshPromise = null;

const refreshAccessToken = async () => {
  const authStore = useAuthStore();
  if (authStore.authMethod === "refresh" && authStore.tokenRefresh) {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refresh_token: authStore.tokenRefresh,
      });
      authStore.setAuthData({
        access_token: response.data.access_token,
        scope: response.data.scope,
        refresh_token: response.data.refresh_token,
      });
      return response.data.access_token;
    } catch (error) {
      if (AUTHORIZATION_TOKEN_DEBUG_ENABLED && env.VUE_APP_DEBUG) {
        console.log("[REFRESH ERROR]:", error);
      }
      authStore.clearAuthData();
      window.location.href = "/login";
      throw error;
    }
  } else if (authStore.storedUsername && authStore.storedPassword) {
    const formData = new FormData();
    formData.append("grant_type", "password");
    formData.append("username", authStore.storedUsername);
    formData.append("password", authStore.storedPassword);

    const response = await axios.post(`${API_BASE_URL}/auth/token`, formData);
    authStore.setAuthData({
      access_token: response.data.access_token,
      scope: response.data.scope,
      username: authStore.storedUsername,
      password: authStore.storedPassword,
    });
    return response.data.access_token;
  }
  throw new Error("No valid refresh mechanism available");
};

apiClient.interceptors.request.use(async (config) => {
  const authStore = useAuthStore();
  if (authStore.isTokenExpired() && authStore.authToken) {
    if (!isRefreshing) {
      if (AUTHORIZATION_TOKEN_DEBUG_ENABLED && env.VUE_APP_DEBUG) {
        console.log("[STARTING TOKEN REFRESH]");
      }
      isRefreshing = true;
      refreshPromise = refreshAccessToken()
        .then((newToken) => {
          isRefreshing = false;
          refreshPromise = null;
          return newToken;
        })
        .catch((error) => {
          isRefreshing = false;
          refreshPromise = null;
          throw error;
        });
    } else {
      if (AUTHORIZATION_TOKEN_DEBUG_ENABLED && env.VUE_APP_DEBUG) {
        console.log("[WAITING FOR REFRESH]:", config.url);
      }
    }

    const newToken = await refreshPromise;
    config.headers["Authorization"] = `Bearer ${newToken}`;
  } else if (authStore.authToken) {
    config.headers["Authorization"] = `Bearer ${authStore.authToken}`;
  }

  if (config.data instanceof FormData) {
    config.headers["Content-Type"] = "multipart/form-data";
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    if (AUTHORIZATION_TOKEN_DEBUG_ENABLED && env.VUE_APP_DEBUG) {
      console.log("[API RESPONSE]:", response);
    }
    return response;
  },
  (error) => {
    if (AUTHORIZATION_TOKEN_DEBUG_ENABLED && env.VUE_APP_DEBUG) {
      console.log("[API ERROR RESPONSE]:", error);
    }
    const errorMessage =
      error.response?.data || error.message || "API request failed";
  
    /**
     * This is unique, only done to check if the device(POS)
     * is associated with the system or not.
     * If not then api server returns 401 Unauthorized with the
     * message(detail) "Unverified client".
     */
    handleUnverifiedDeviceClient(error);

    return Promise.reject(errorMessage);
  }
);

export const apiService = {
  login: async (username, password) => {
    const formData = new FormData();
    formData.append("grant_type", "password");
    formData.append("username", username);
    formData.append("password", password);

    const response = await axios.post(`${API_BASE_URL}/auth/token`, formData);
    const authStore = useAuthStore();
    authStore.setAuthData({
      access_token: response.data.access_token,
      scope: response.data.scope,
      username,
      password,
      refresh_token: response.data.refresh_token,
    });
    return response.data;
  },
  get: async (endpoint, params = {}) => {
    const response = await apiClient.get(endpoint, { params });
    return response.data;
  },
  post: async (endpoint, data, hasFile = false) => {
    const headers = hasFile ? { "Content-Type": "multipart/form-data" } : {};
    const response = await apiClient.post(endpoint, data, { headers });
    return response.data;
  },
  put: async (endpoint, data, hasFile = false) => {
    const headers = hasFile ? { "Content-Type": "multipart/form-data" } : {};
    const response = await apiClient.put(endpoint, data, { headers });
    return response.data;
  },
  patch: async (endpoint, data = null, hasFile = false) => {
    const headers = hasFile ? { "Content-Type": "multipart/form-data" } : {};
    const response = await apiClient.patch(endpoint, data, { headers });
    return response.data;
  },
  delete: async (endpoint) => {
    const response = await apiClient.delete(endpoint);
    return response.data;
  },
};
